// SSR-safe storage wrapper for shared/storage.
// See react-rewrite-plan.md sections 8.3 and 11.2 platform-storage.
//
// All methods are no-ops (returning the supplied fallback or doing nothing)
// when storage is unavailable. This keeps SSR render paths and private-mode
// browsers from crashing.
//
// Failures are reported through shared/logger at debug level (not warn),
// because they are expected in SSR and private-mode contexts and should
// not look like errors in dev tools.

import { log } from '../logger';
import { isStorageAvailable, type StorageKind } from './isAvailable';
import { safeParseJson, safeStringify } from './safeJson';

export interface SafeStorage {
  get<T>(key: string, fallback: T): T;
  set(key: string, value: unknown): void;
  remove(key: string): void;
}

function pickStorage(kind: StorageKind): Storage | null {
  if (typeof window === 'undefined') {
    return null;
  }
  return kind === 'local' ? localStorage : sessionStorage;
}

export function createStorage(kind: StorageKind): SafeStorage {
  const available = isStorageAvailable(kind);
  const storage = available ? pickStorage(kind) : null;

  return {
    get<T>(key: string, fallback: T): T {
      if (storage === null) {
        return fallback;
      }
      try {
        const raw = storage.getItem(key);
        return safeParseJson<T>(raw, fallback);
      } catch (err) {
        log.debug('shared/storage: getItem failed', { extra: { key, kind, error: String(err) } });
        return fallback;
      }
    },
    set(key: string, value: unknown): void {
      if (storage === null) {
        return;
      }
      const serialized = safeStringify(value);
      if (serialized === null) {
        log.debug('shared/storage: value not serializable', { extra: { key, kind } });
        return;
      }
      try {
        storage.setItem(key, serialized);
      } catch (err) {
        // Quota exceeded or storage was revoked mid-session.
        log.debug('shared/storage: setItem failed', { extra: { key, kind, error: String(err) } });
      }
    },
    remove(key: string): void {
      if (storage === null) {
        return;
      }
      try {
        storage.removeItem(key);
      } catch (err) {
        log.debug('shared/storage: removeItem failed', { extra: { key, kind, error: String(err) } });
      }
    },
  };
}
